import { Controller } from "react-hook-form";
import TextInput from "../inputs/TextInput";
import ComboboxInput from "../inputs/ComboboxInput";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useGetAllNationalities,
  useGetDocumentIDTypes,
} from "../../hooks/common/query";
import { mapToOptions } from "../../lib/functions";
import {
  DOC_TYPE_ID,
  USER_ROLES_IDS,
  UserAppointment,
} from "../../constants/common";
import Accordion from "../accordian";
import { useTranslation } from "react-i18next";
import DatePickerInput from "../inputs/DatePickerInput";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { APP_ROUTES } from "../../routes";
import CheckboxInput from "../inputs/CheckboxInput";
import useAuthStore from "../../store/Auth";

interface VisitorDetailsProps {
  control: any;
  errors: any;
  selectedIdType?: number;
  userAppointmentDetails?: UserAppointment;
  type?: string;
  isDisplayType?: boolean;
  isVIP?: boolean;

  isVehicleInfoVisible: boolean;
  setIsVehicleInfoVisible: Dispatch<SetStateAction<boolean>>;
}

const VisitorDetailsCard = ({
  control,
  errors,
  selectedIdType,
  userAppointmentDetails,
  isVIP = false,

  isVehicleInfoVisible,
  setIsVehicleInfoVisible,
}: VisitorDetailsProps) => {
  const [selectedKey, setSelectedKey] = useState<string>("1");

  const [nationalities, setNationalities] = useState<
    { labelEn: string; labelAr: string; value: any }[]
  >([]);
  const { data: allNationalities } = useGetAllNationalities();

  const user = useAuthStore((state) => state.user);

  const { t } = useTranslation();

  const location = useLocation();

  const isApprovalReq = useMemo(
    () => location.pathname === APP_ROUTES.APPROVE_REQUEST,
    [location.pathname]
  );

  useEffect(() => {
    if (allNationalities) {
      const mappedNationalities = mapToOptions(
        "name",
        "name_Ar",
        "iso3",
        allNationalities
      );
      setNationalities(mappedNationalities);
    }
  }, [allNationalities]);

  const { data: allDocumentIDTypes } = useGetDocumentIDTypes();

  const documentIDTypes = useMemo(
    () =>
      mapToOptions(
        "s_Desc_E",
        "s_Desc_A",
        "n_DetailedCode",
        allDocumentIDTypes
      ),
    [allDocumentIDTypes]
  );

  return (
    <div className="bg-white p-2 md:p-4 z-50 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordion
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 items-end -mt-2">
            {t("1")}. {t("visitorDetails")}
          </h1>
        }
      >
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 lg:gap-4">
          <Controller
            name="name"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("visitorName")}
                value={value ?? userAppointmentDetails?.name ?? ""}
                onChange={onChange}
                error={errors?.[name]?.message}
                required
                readOnly={isApprovalReq}
              />
            )}
          />
          <Controller
            name="companyName"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("companyName")}
                onChange={onChange}
                value={value ?? userAppointmentDetails?.companyName ?? ""}
                error={errors?.[name]?.message}
                required={!isVIP}
                readOnly={isApprovalReq}
              />
            )}
          />
          <Controller
            name="nationality"
            control={control}
            render={({ field: { name, onChange, value } }) => {
              return (
                <ComboboxInput
                  options={nationalities}
                  onChange={onChange}
                  value={
                    nationalities?.find((item) => item.value === value)
                      ?.labelEn ||
                    nationalities?.find(
                      (item) =>
                        item.value === userAppointmentDetails?.nationality
                    )?.labelEn ||
                    ""
                  }
                  error={errors?.[name] && t("nationalityRequired")}
                  label={t("nationality")}
                  required={!isVIP}
                  readOnly={isApprovalReq}
                />
              );
            }}
          />
          <Controller
            name="number"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <div className="flex flex-col">
                <TextInput
                  label={t("phoneNumber")}
                  value={value ?? userAppointmentDetails?.number}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  required={!isVIP}
                  readOnly={isApprovalReq}
                  isInfoToolTip
                  infoToolTip={<p>{t("enterPhoneNumber")}</p>}
                />
              </div>
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("email")}
                value={value ?? userAppointmentDetails?.email ?? ""}
                onChange={onChange}
                error={errors?.[name]?.message}
                required
                readOnly={isApprovalReq}
              />
            )}
          />
          <Controller
            name="idType"
            control={control}
            render={({ field: { name, onChange, value } }) => {
              return (
                <ComboboxInput
                  options={documentIDTypes}
                  onChange={(value) => onChange(value === "" ? null : value)}
                  error={errors?.[name] && t("idTypeRequired")}
                  value={
                    value === DOC_TYPE_ID.NATIONAL_ID ||
                    userAppointmentDetails?.idType === DOC_TYPE_ID.NATIONAL_ID
                      ? "National ID"
                      : value === DOC_TYPE_ID.PASSPORT ||
                        userAppointmentDetails?.idType === DOC_TYPE_ID.PASSPORT
                      ? "Passport"
                      : value === DOC_TYPE_ID.IQAMA ||
                        userAppointmentDetails?.idType === DOC_TYPE_ID.IQAMA
                      ? "Iqama"
                      : value === DOC_TYPE_ID.OTHER ||
                        userAppointmentDetails?.idType === DOC_TYPE_ID.OTHER
                      ? "Other"
                      : value === DOC_TYPE_ID.VISA ||
                        userAppointmentDetails?.idType === DOC_TYPE_ID.VISA
                      ? "Visa"
                      : ""
                  }
                  label={t("idType")}
                  required={!isVIP}
                  readOnly={isApprovalReq}
                />
              );
            }}
          />
          {selectedIdType === DOC_TYPE_ID.IQAMA && (
            <>
              <Controller
                name="iqama"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("iqamaNumber")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    required={!isVIP}
                    value={value ?? userAppointmentDetails?.iqama ?? ""}
                    readOnly={isApprovalReq}
                    isInfoToolTip
                    infoToolTip={<p>{t("enterIqamaNumber")}</p>}
                  />
                )}
              />
              <Controller
                name="iqamaExpiryDate"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <DatePickerInput
                    label={t("iqamaExpiryDate")}
                    value={
                      value ??
                      (userAppointmentDetails?.iqamaExpiryDate &&
                        moment(
                          new Date(userAppointmentDetails?.iqamaExpiryDate)
                        ).format("MM/DD/YYYY")) ??
                      ""
                    }
                    required={!isVIP}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    readOnly={isApprovalReq}
                    minDate={
                      location.pathname ===
                        APP_ROUTES.INDIVIDUAL_VISIT_REQUEST ||
                      location.pathname === APP_ROUTES.VIP_VISIT_REQUEST ||
                      location.pathname === APP_ROUTES.GROUP_VISIT_REQUEST
                        ? new Date()
                        : undefined
                    }
                  />
                )}
              />
            </>
          )}

          {(!selectedIdType || selectedIdType === DOC_TYPE_ID.NATIONAL_ID) && (
            <>
              <Controller
                name="eid"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("nationalID")}
                    value={value ?? userAppointmentDetails?.eid ?? ""}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    required={!isVIP}
                    readOnly={isApprovalReq}
                    isInfoToolTip
                    infoToolTip={<p>{t("enterNationalId")}</p>}
                  />
                )}
              />
              <Controller
                name="eidExpiryDate"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <DatePickerInput
                    label={t("nationalIDExpiryDate")}
                    value={
                      value ??
                      (userAppointmentDetails?.eidExpiryDate &&
                        moment(
                          new Date(userAppointmentDetails?.eidExpiryDate)
                        ).format("MM/DD/YYYY")) ??
                      ""
                    }
                    required={!isVIP}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    readOnly={isApprovalReq}
                    minDate={
                      location.pathname ===
                        APP_ROUTES.INDIVIDUAL_VISIT_REQUEST ||
                      location.pathname === APP_ROUTES.VIP_VISIT_REQUEST ||
                      location.pathname === APP_ROUTES.GROUP_VISIT_REQUEST
                        ? new Date()
                        : undefined
                    }
                  />
                )}
              />
            </>
          )}

          {selectedIdType === DOC_TYPE_ID.PASSPORT && (
            <>
              <Controller
                name="passportNumber"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("passportNumber")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    required={!isVIP}
                    value={
                      value ?? userAppointmentDetails?.passportNumber ?? ""
                    }
                    readOnly={isApprovalReq}
                  />
                )}
              />
              <Controller
                name="passportExpiryDate"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <DatePickerInput
                    label={t("passportExpiryDate")}
                    value={
                      value ??
                      (userAppointmentDetails?.passportExpiryDate &&
                        moment(
                          new Date(userAppointmentDetails?.passportExpiryDate)
                        ).format("MM/DD/YYYY")) ??
                      ""
                    }
                    required={!isVIP}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    readOnly={isApprovalReq}
                    minDate={
                      location.pathname ===
                        APP_ROUTES.INDIVIDUAL_VISIT_REQUEST ||
                      location.pathname === APP_ROUTES.VIP_VISIT_REQUEST ||
                      location.pathname === APP_ROUTES.GROUP_VISIT_REQUEST
                        ? new Date()
                        : undefined
                    }
                  />
                )}
              />
            </>
          )}

          {selectedIdType === DOC_TYPE_ID.VISA && (
            <>
              <Controller
                name="visaNumber"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("visaNumber")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    required={!isVIP}
                    value={value ?? userAppointmentDetails?.visaNumber ?? ""}
                    readOnly={isApprovalReq}
                  />
                )}
              />
              <Controller
                name="visaExpiryDate"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <DatePickerInput
                    label={t("visaExpiryDate")}
                    value={
                      value ??
                      (userAppointmentDetails?.visaExpiryDate &&
                        moment(
                          new Date(userAppointmentDetails?.visaExpiryDate)
                        ).format("MM/DD/YYYY")) ??
                      ""
                    }
                    required={!isVIP}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    readOnly={isApprovalReq}
                    minDate={
                      location.pathname ===
                        APP_ROUTES.INDIVIDUAL_VISIT_REQUEST ||
                      location.pathname === APP_ROUTES.VIP_VISIT_REQUEST ||
                      location.pathname === APP_ROUTES.GROUP_VISIT_REQUEST
                        ? new Date()
                        : undefined
                    }
                  />
                )}
              />
            </>
          )}

          {selectedIdType === DOC_TYPE_ID.OTHER && (
            <>
              <Controller
                name="idTypeOtherName"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("documentNameOther")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    required={!isVIP}
                    value={
                      value ?? userAppointmentDetails?.idTypeOtherName ?? ""
                    }
                    readOnly={isApprovalReq}
                  />
                )}
              />
              <Controller
                name="idTypeOtherNumber"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <TextInput
                    label={t("documentNumberOther")}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    required={!isVIP}
                    value={
                      value ?? userAppointmentDetails?.idTypeOtherNumber ?? ""
                    }
                    readOnly={isApprovalReq}
                  />
                )}
              />
              <Controller
                name="idTypeOtherExpiryDate"
                control={control}
                render={({ field: { name, value, onChange } }) => (
                  <DatePickerInput
                    label={t("documentExpiryDateOther")}
                    value={
                      value ??
                      (userAppointmentDetails?.idTypeOtherExpiryDate &&
                        moment(
                          new Date(
                            userAppointmentDetails?.idTypeOtherExpiryDate
                          )
                        ).format("MM/DD/YYYY")) ??
                      ""
                    }
                    required={!isVIP}
                    onChange={onChange}
                    error={errors?.[name]?.message}
                    readOnly={isApprovalReq}
                    minDate={
                      location.pathname ===
                        APP_ROUTES.INDIVIDUAL_VISIT_REQUEST ||
                      location.pathname === APP_ROUTES.VIP_VISIT_REQUEST ||
                      location.pathname === APP_ROUTES.GROUP_VISIT_REQUEST
                        ? new Date()
                        : undefined
                    }
                  />
                )}
              />
            </>
          )}

          {/* <Controller
            name="vehicleNo"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <TextInput
                label={t("vehicleNo")}
                value={value ?? userAppointmentDetails?.vehicleNo ?? ""}
                onChange={onChange}
                error={errors?.[name]?.message}
                readOnly={isApprovalReq}
                placeholder={
                  location.pathname !== APP_ROUTES.APPROVE_REQUEST &&
                  location.pathname !== APP_ROUTES.UPDATE_REQUEST
                    ? i18n.language === "en"
                      ? "799 LJK"
                      : "ك ج ل ٧٩٩"
                    : ""
                }
              />
            )}
          /> */}

          <Controller
            name="dateOfBirth"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <DatePickerInput
                label={t("dateOfBirth")}
                value={
                  value ??
                  (userAppointmentDetails?.dateOfBirth &&
                    moment(
                      new Date(userAppointmentDetails?.dateOfBirth)
                    ).format("MM/DD/YYYY")) ??
                  ""
                }
                required={!isVIP}
                onChange={onChange}
                error={errors?.[name]?.message}
                readOnly={isApprovalReq}
                maxDate={new Date()}
              />
            )}
          />      

        </div>

        <div className="mt-4">
          <CheckboxInput
            id="myself-vehicle-details"
            label={t("vehicleDetails")}
            checked={isVehicleInfoVisible}
            setChecked={setIsVehicleInfoVisible}
            readOnly={user?.n_RoleID !== USER_ROLES_IDS.STAFF}
          />
        </div>
      </Accordion>
    </div>
  );
};

export default React.memo(VisitorDetailsCard);
